import { definedOnly, pickDefined, type WidgetDataBag } from '../patch-helpers';
import { LOGO_SOURCE_KEYS, LOGO_STYLE_KEYS, type PartialLogoWidgetData } from './partial-logo-widget-data.schema';

const SOURCE_KEYS: ReadonlySet<string> = new Set(LOGO_SOURCE_KEYS);
const STYLE_KEYS: ReadonlySet<string> = new Set(LOGO_STYLE_KEYS);

/**
 * Splits the flat AI patch back across the two levels of the real entity — `data.image` (the
 * file-manager pick) and the envelope's `styles` group. See `partialLogoWidgetDataSchema` for why
 * the AI surface is flat, and `image-widget-patch.ts` for the twin this is trimmed down from:
 * logo has one source key (`url`, no `alt`/`link`/dimensions) and two plain style numbers (no
 * per-corner radius — unlike image, `logoStyleBagSchemaLegacy` has a single `borderRadius`, so
 * there is no four-corner write to mirror here).
 *
 * `storedFile` is what `resolveImageSources` collected for this patch's `url` — the ~40-field pick
 * a model has no way to author on its own. Without it, a logo set from a raw URL would be only
 * half a picked image: it would still render, but the file-manager panel would show "choose a
 * file" the same way an unresolved image widget does.
 */
export function foldLogoWidgetPatch(
    currentData: WidgetDataBag,
    currentStyles: WidgetDataBag,
    patch: PartialLogoWidgetData,
    storedFile?: WidgetDataBag | null
): { data: WidgetDataBag; styles: WidgetDataBag } {
    const flat = patch as WidgetDataBag;
    const currentImage = currentData['image'] as WidgetDataBag | null | undefined;

    const sourcePatch = pickDefined(flat, LOGO_SOURCE_KEYS);
    const stylePatch = pickDefined(flat, LOGO_STYLE_KEYS);
    const dataPatch = definedOnly(
        Object.fromEntries(Object.entries(flat).filter(([key]) => !SOURCE_KEYS.has(key) && !STYLE_KEYS.has(key)))
    );

    const nextData: WidgetDataBag = { ...currentData, ...dataPatch };

    if (Object.keys(sourcePatch).length || storedFile) {
        nextData['image'] = storedFile
            ? { ...storedFile, ...sourcePatch, url: storedFile['url'] }
            : { ...(currentImage ?? {}), ...sourcePatch };
    }

    const nextStyles: WidgetDataBag = { ...currentStyles, ...stylePatch };

    return { data: nextData, styles: nextStyles };
}
